/**
 * Session Timer Utility
 *
 * Solves the problem of client system clocks being incorrect by using:
 * 1. Monotonic time (performance.now()) for in-session countdown — immune to clock changes
 * 2. Server clock offset computed from login response headers
 * 3. Session duration derived from token's (exp - iat) — purely server-determined
 *
 * On page reload, we recalculate elapsed time using the stored wall-clock login time
 * adjusted by the server clock offset.
 */

const STORAGE_KEY = "__session_timing__";

let _monoLoginTime = null; // performance.now() when session was established
let _sessionDurationMs = null; // total session length in ms (from exp - iat)
let _serverClockOffsetMs = 0; // serverTime - clientTime (positive = client behind)

function persist() {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        wallClockLoginMs: Date.now() - (performance.now() - _monoLoginTime),
        sessionDurationMs: _sessionDurationMs,
        serverClockOffsetMs: _serverClockOffsetMs,
      })
    );
  } catch {}
}

/**
 * Initialize session timing after a successful login.
 * @param {object} decoded - decoded JWT payload (must have exp; iat is optional)
 * @param {object} [responseHeaders] - axios response headers to extract server Date
 */
export function initSession(decoded, responseHeaders) {
  const exp = Number(decoded.exp) || 0;
  const iat = Number(decoded.iat) || 0;

  // Compute server clock offset from response Date header if available
  if (responseHeaders) {
    const serverDateStr = responseHeaders["date"] || responseHeaders["Date"];
    if (serverDateStr) {
      const serverTimeMs = new Date(serverDateStr).getTime();
      if (!isNaN(serverTimeMs)) {
        _serverClockOffsetMs = serverTimeMs - Date.now();
      }
    }
  }

  // Session duration: prefer (exp - iat) from server; fallback to 1 hour
  if (exp && iat && exp > iat) {
    _sessionDurationMs = (exp - iat) * 1000;
  } else if (exp) {
    // If iat is missing, compute duration using offset-adjusted current time
    const adjustedNowSec = (Date.now() + _serverClockOffsetMs) / 1000;
    const durationSec = exp - adjustedNowSec;
    _sessionDurationMs = durationSec > 0 ? durationSec * 1000 : 3600_000;
  } else {
    _sessionDurationMs = 3600_000; // default 1 hour
  }

  _monoLoginTime = performance.now();
  persist();
}

/**
 * Restore session timing on page reload (cold start).
 * Returns false if session data is missing or already expired.
 */
export function restoreSessionTiming() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;

    const { wallClockLoginMs, sessionDurationMs, serverClockOffsetMs } = JSON.parse(raw);
    if (!wallClockLoginMs || !sessionDurationMs) return false;

    _serverClockOffsetMs = serverClockOffsetMs || 0;
    _sessionDurationMs = sessionDurationMs;

    // Calculate how much time has elapsed since login using offset-adjusted clock
    const adjustedNow = Date.now() + _serverClockOffsetMs;
    const adjustedLoginTime = wallClockLoginMs + _serverClockOffsetMs;
    const elapsedMs = adjustedNow - adjustedLoginTime;

    if (elapsedMs >= sessionDurationMs) {
      clearSession();
      return false;
    }

    // Set mono login time as if login happened `elapsedMs` ago
    _monoLoginTime = performance.now() - elapsedMs;
    return true;
  } catch {
    return false;
  }
}

/**
 * Get remaining session time in milliseconds.
 * Uses monotonic clock — completely immune to system clock changes.
 */
export function getRemainingMs() {
  if (_monoLoginTime == null || _sessionDurationMs == null) return null;
  const elapsed = performance.now() - _monoLoginTime;
  const remaining = _sessionDurationMs - elapsed;
  return remaining > 0 ? remaining : 0;
}

/**
 * Check whether the session has expired.
 */
export function isSessionExpired() {
  const remaining = getRemainingMs();
  return remaining !== null && remaining <= 0;
}

/**
 * Get the total session duration (for progress bar calculations).
 */
export function getSessionDurationMs() {
  return _sessionDurationMs;
}

/**
 * Clear all session timing state.
 */
export function clearSession() {
  _monoLoginTime = null;
  _sessionDurationMs = null;
  _serverClockOffsetMs = 0;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}
